/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  GetJobRunPathParameters,
  ListJobRunsParams,
  ListJobRunsPathParameters,
  ListJobSchedulesParams,
  TLHTTPValidationError,
  TLJobRunDetailResponse,
  TLPaginatedResponseJobRunResponse,
  TLPaginatedResponseJobScheduleResponse,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary List Job Schedules
 */
export const listJobSchedules = (
  params?: ListJobSchedulesParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponseJobScheduleResponse>(
    { url: `/jobs/schedules`, method: 'GET', params, signal },
    options,
  );
};

export const getListJobSchedulesQueryKey = (params?: ListJobSchedulesParams) => {
  return ['jobs', 'schedules', ...(params ? [params] : [])] as const;
};

export const getListJobSchedulesQueryOptions = <
  TData = Awaited<ReturnType<typeof listJobSchedules>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListJobSchedulesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listJobSchedules>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListJobSchedulesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listJobSchedules>>> = ({ signal }) =>
    listJobSchedules(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listJobSchedules>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListJobSchedulesQueryResult = NonNullable<Awaited<ReturnType<typeof listJobSchedules>>>;
export type ListJobSchedulesQueryError = ErrorType<TLHTTPValidationError>;

export function useListJobSchedules<
  TData = Awaited<ReturnType<typeof listJobSchedules>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListJobSchedulesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listJobSchedules>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listJobSchedules>>,
          TError,
          Awaited<ReturnType<typeof listJobSchedules>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListJobSchedules<
  TData = Awaited<ReturnType<typeof listJobSchedules>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListJobSchedulesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listJobSchedules>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listJobSchedules>>,
          TError,
          Awaited<ReturnType<typeof listJobSchedules>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListJobSchedules<
  TData = Awaited<ReturnType<typeof listJobSchedules>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListJobSchedulesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listJobSchedules>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Job Schedules
 */

export function useListJobSchedules<
  TData = Awaited<ReturnType<typeof listJobSchedules>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListJobSchedulesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listJobSchedules>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListJobSchedulesQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Job Schedules
 */
export const prefetchListJobSchedulesQuery = async <
  TData = Awaited<ReturnType<typeof listJobSchedules>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListJobSchedulesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listJobSchedules>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListJobSchedulesQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Job Schedules
 */
export const invalidateListJobSchedules = async (
  queryClient: QueryClient,
  params?: ListJobSchedulesParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListJobSchedulesQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary List Job Runs
 */
export const listJobRuns = (
  { jobName }: ListJobRunsPathParameters,
  params?: ListJobRunsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponseJobRunResponse>(
    { url: `/jobs/${jobName}/runs`, method: 'GET', params, signal },
    options,
  );
};

export const getListJobRunsQueryKey = ({ jobName }: ListJobRunsPathParameters, params?: ListJobRunsParams) => {
  return ['jobs', jobName, 'runs', ...(params ? [params] : [])] as const;
};

export const getListJobRunsQueryOptions = <
  TData = Awaited<ReturnType<typeof listJobRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobName }: ListJobRunsPathParameters,
  params?: ListJobRunsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listJobRuns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListJobRunsQueryKey({ jobName }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listJobRuns>>> = ({ signal }) =>
    listJobRuns({ jobName }, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: jobName !== null && jobName !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listJobRuns>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListJobRunsQueryResult = NonNullable<Awaited<ReturnType<typeof listJobRuns>>>;
export type ListJobRunsQueryError = ErrorType<TLHTTPValidationError>;

export function useListJobRuns<
  TData = Awaited<ReturnType<typeof listJobRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListJobRunsPathParameters,
  params: undefined | ListJobRunsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listJobRuns>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listJobRuns>>,
          TError,
          Awaited<ReturnType<typeof listJobRuns>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListJobRuns<
  TData = Awaited<ReturnType<typeof listJobRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListJobRunsPathParameters,
  params?: ListJobRunsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listJobRuns>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listJobRuns>>,
          TError,
          Awaited<ReturnType<typeof listJobRuns>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListJobRuns<
  TData = Awaited<ReturnType<typeof listJobRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListJobRunsPathParameters,
  params?: ListJobRunsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listJobRuns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Job Runs
 */

export function useListJobRuns<
  TData = Awaited<ReturnType<typeof listJobRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobName }: ListJobRunsPathParameters,
  params?: ListJobRunsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listJobRuns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListJobRunsQueryOptions({ jobName }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Job Runs
 */
export const prefetchListJobRunsQuery = async <
  TData = Awaited<ReturnType<typeof listJobRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { jobName }: ListJobRunsPathParameters,
  params?: ListJobRunsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listJobRuns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListJobRunsQueryOptions({ jobName }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Job Runs
 */
export const invalidateListJobRuns = async (
  queryClient: QueryClient,
  { jobName }: ListJobRunsPathParameters,
  params?: ListJobRunsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListJobRunsQueryKey({ jobName }, params) }, options);

  return queryClient;
};

/**
 * @summary Get Job Run
 */
export const getJobRun = (
  { jobName, runId }: GetJobRunPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLJobRunDetailResponse>({ url: `/jobs/${jobName}/runs/${runId}`, method: 'GET', signal }, options);
};

export const getGetJobRunQueryKey = ({ jobName, runId }: GetJobRunPathParameters) => {
  return ['jobs', jobName, 'runs', runId] as const;
};

export const getGetJobRunQueryOptions = <
  TData = Awaited<ReturnType<typeof getJobRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobName, runId }: GetJobRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getJobRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetJobRunQueryKey({ jobName, runId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getJobRun>>> = ({ signal }) =>
    getJobRun({ jobName, runId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: jobName !== null && jobName !== undefined && runId !== null && runId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getJobRun>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetJobRunQueryResult = NonNullable<Awaited<ReturnType<typeof getJobRun>>>;
export type GetJobRunQueryError = ErrorType<TLHTTPValidationError>;

export function useGetJobRun<TData = Awaited<ReturnType<typeof getJobRun>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetJobRunPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getJobRun>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof getJobRun>>, TError, Awaited<ReturnType<typeof getJobRun>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetJobRun<TData = Awaited<ReturnType<typeof getJobRun>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetJobRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getJobRun>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getJobRun>>,
          TError,
          Awaited<ReturnType<typeof getJobRun>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetJobRun<TData = Awaited<ReturnType<typeof getJobRun>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetJobRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getJobRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Job Run
 */

export function useGetJobRun<TData = Awaited<ReturnType<typeof getJobRun>>, TError = ErrorType<TLHTTPValidationError>>(
  { jobName, runId }: GetJobRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getJobRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetJobRunQueryOptions({ jobName, runId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Job Run
 */
export const prefetchGetJobRunQuery = async <
  TData = Awaited<ReturnType<typeof getJobRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { jobName, runId }: GetJobRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getJobRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetJobRunQueryOptions({ jobName, runId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Job Run
 */
export const invalidateGetJobRun = async (
  queryClient: QueryClient,
  { jobName, runId }: GetJobRunPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetJobRunQueryKey({ jobName, runId }) }, options);

  return queryClient;
};
